/**
 * Push adapter.
 *
 * @module app/components/PushAdapter
 */
import { subscribe, unsubscribe, unsubscribeAll } from '@push-component/client';


import {
    SPORTS,
    PRICE,
    CASHOUT_PRICE,
    IS_IN_PLAY,
    EACH_WAY,
    HANDICAP,
    STATUS,
    NAME,
    ORDER,
    DISPLAYED,
    SETTLED,
    START_DATETIME,
    OFFTIME,
    MARKET_GROUP,
    BLURB,
    SORT,
    HAS_IN_PLAY,
    ACTIVE,
    CORRECT_SCORE,
    FB_RESULT,
} from '@push-component/constants';

import DataUpdates from './domUpdates/DataUpdates';
import MessageBusTopics from '../../../common/MessageBusTopics';

const OWNER = 'sportsbook-web';

/**
 * Logger
 *
 * @type    {Function}
 *
 * @private
 */
var log = function () {
};

/**
 * Module state.
 *
 * @type {Object}
 *
 * @private
 */
var state = {
    dataUpdates: DataUpdates,
};

/**
 * Callback for push messages.
 *
 * @param {Object} message The message.
 *
 * @returns {void}
 *
 * @private
 */
var onMessage = function (message, initialTopic, topic) {

    state.dataUpdates.update(message, initialTopic, topic);
};

/**
 * Prepare state
 *
 * @param   {Object} deps
 *
 * @returns {void}
 *
 * @private
 */
var prepareState = function (deps) {

    /**
     * Set the sports data repository from the dependencies.
     *
     * @param {Object} deps Dependencies.
     *
     * @returns {void}
     * @private
     */
    var setDataUpdates = function (deps) {

        state.dataUpdates = deps.dataUpdates || DataUpdates;
    };

    setDataUpdates(deps);
    state.dataUpdates.DomManipulator.setCallback(deps.navigate);

    publishPDSDiffusion();
};

/**
 * Publish a handle to the PDSDiffusion instance.
 *
 * @returns {void}
 * @private
 */
var publishPDSDiffusion = function publishPDSDiffusion() {

    if (window.WH && window.WH.messageBus) {
        window.WH.messageBus.publishSticky(MessageBusTopics.PUSH_PDS_DIFFUSION, {
            subscribe: api.subscribe,
            unsubscribe: api.unsubscribe,
        });
    }
};

/**
 * Adds a field to the push fields map
 *
 * @param {Object} options Options
 *
 * @returns {void}
 * @private
 */
const addPushField = ({ fields, fieldValue, fieldName, prefix }) => {
    if (fieldValue === undefined) {
        return;
    }

    if (fieldValue === prefix + '.*') {
        fields[fieldName] = '*';
        return;
    }

    fields[fieldName] = fieldValue;
};

/**
 * Returns the push fields
 *
 * @param {string} topic Diffusion topic
 *
 * @returns {Object}
 * @private
 */
const getPushFields = (topic) => {
    const fields = {};
    const parts = topic.split('/');

    addPushField({
        fields,
        fieldValue: parts[1],
        fieldName: 'eventId',
        prefix: 'OB_EV',
    });

    addPushField({
        fields,
        fieldValue: parts[2],
        fieldName: 'marketId',
        prefix: 'OB_MA',
    });

    addPushField({
        fields,
        fieldValue: parts[3],
        fieldName: 'selectionId',
        prefix: 'OB_OU',
    });

    return fields;
};

/**
 * Returns the diffusion topic
 *
 * @param {Object} fields Push component fields
 *
 * @returns {string}
 * @private
 */
const getPushTopic = (fields) => {
    if (fields.selectionId) {
        return `PDS/${fields.eventId}/${fields.marketId}/${fields.selectionId}`;
    }

    if (fields.marketId) {
        return `PDS/${fields.eventId}/${fields.marketId}`;
    }

    return `PDS/${fields.eventId}`;
};

const updateMapping = {
    [NAME]: {
        name: ['name'],
    },
    [ORDER]: {
        order: ['order'],
    },
    [DISPLAYED]: {
        displayed: ['displayed'],
    },
    [SETTLED]: {
        settled: ['settled'],
    },
    [START_DATETIME]: {
        startDateTime: ['startDateTime'],
    },
    [MARKET_GROUP]: {
        marketGroupId: ['marketGroupId'],
        marketGroupCollectionId: ['marketGroupCollectionId'],
    },
    [BLURB]: {
        blurb: ['blurb'],
    },
    [SORT]: {
        sort: ['sort'],
    },
    [HAS_IN_PLAY]: {
        hasInPlay: ['hasInPlay'],
    },
    [ACTIVE]: {
        active: ['active'],
    },
    [CORRECT_SCORE]: {
        csHome: ['csHome'],
        csAway: ['csAway'],
    },
    [FB_RESULT]: {
        fbResult: ['fbResult'],
    },
    [PRICE]: {
        numerator: ['currentPriceNum'],
        denominator: ['currentPriceDen'],
    },
    [CASHOUT_PRICE]: {
        cashoutNumerator: ['cashoutPriceNum'],
        cashoutDenominator: ['cashoutPriceDen'],
    },
    [EACH_WAY]: {
        available: ['eachWay'],
        places: ['eachWayPlaces'],
        numerator: ['eachWayFactorNum'],
        denominator: ['eachWayFactorDen'],
    },
    [STATUS]: {
        status: ['status'],
    },
    [HANDICAP]: {
        value: ['hcapValue'],
    },
    [IS_IN_PLAY]: {
        value: ['isInPlay', 'betInRun'],
    },
    [OFFTIME]: {
        offtime: ['offtime'],
    },
};

/**
 * Handles updates
 *
 * @param {Object} update Push component update
 * @param {string} id Entity ID
 * @param {string} childTopic Child topic
 * @param {string} topic Parent topic
 *
 * @returns {string}
 * @private
 */
const handleUpdate = (update, id, childTopic, topic) => {
    if (updateMapping.hasOwnProperty(update.type)) {
        const mappings = updateMapping[update.type];

        Object.keys(mappings).forEach((key) => {
            const value = update[key];
            mappings[key].forEach((attribute) => {
                if (value !== undefined) {
                    onMessage({ id, attribute, value: value + '' }, topic, childTopic);
                }
            });
        });
    }

    if (update.type === SETTLED && update.settled) {
        onMessage({
            id,
            attribute: 'displayed',
            value: 'false',
        }, topic, childTopic);
    }
};

/**
 * Methods to be public
 *
 * @type {Object}
 */
var api = {

    /**
     * Initialise things.
     *
     * @param {Object} deps
     *
     * @returns {void}
     *
     * @public
     */
    init: function (deps) {

        log('PushAdapter.init()');

        prepareState(deps);

        window.SB = window.SB || {};
        window.SB.push = function (id, attribute, value) {

            onMessage({
                id: id,
                attribute: attribute,
                value: value,
            });
        };
    },

    /**
     * Connects to diffusion
     *
     * @returns {void}
     *
     * @public
     */
    connect: function () {
        log('PushAdapter.connect()');
    },

    /**
     * Disconnects from diffusion
     *
     * @returns {void}
     *
     * @public
     */
    disconnect: function () {
        log('PushAdapter.disconnect()');
    },

    /**
     * Subscribes to a topic
     *
     * @param {String} topic
     *
     * @returns {void}
     *
     * @public
     */
    subscribe: function (topic) {

        log('PushAdapter.subscribe(' + topic + ')');

        let currentPrice = {};

        /**
         * Emits a custom "currentprice" update for legacy purposes
         *
         * @param {Object} update Push component update
         * @param {string} childTopic Child topic
         *
         * @returns {string}
         * @private
         */
        const handleCurrentPrice = (update, childTopic) => {
            if (update.type === PRICE) {
                currentPrice = { ...currentPrice, ...update };
                onMessage({
                    id: update.selectionId,
                    attribute: 'currentprice',
                    value: `${currentPrice.numerator}/${currentPrice.denominator}`,
                }, topic, childTopic);
            }
        };

        subscribe({
            type: SPORTS,
            owner: OWNER,
            ...getPushFields(topic),
            callback: (update) => {
                const childTopic = getPushTopic(update);

                if (update.selectionId) {
                    handleUpdate(update, update.selectionId, childTopic, topic);
                    handleCurrentPrice(update, childTopic);
                    return;
                }

                if (update.marketId) {
                    return handleUpdate(update, update.marketId, childTopic, topic);
                }

                return handleUpdate(update, update.eventId, childTopic, topic);
            },
        });
    },

    /**
     * Unsubscribes to a topic
     *
     * @param {String} topic
     *
     * @returns {void}
     *
     * @public
     */
    unsubscribe: function (topic) {

        log('PushAdapter.unsubscribe(' + topic + ')');

        unsubscribe({
            type: SPORTS,
            owner: OWNER,
            ...getPushFields(topic),
        });
    },

    /**
     * Unsubscribes to all topics
     *
     * @returns {void}
     *
     * @public
     */
    unsubscribeAll: function () {

        log('PushAdapter.unsubscribeAll()');

        unsubscribeAll({
            type: SPORTS,
            owner: OWNER,
        });
    },
};

export default api;
